// ============================================================
// pages/admin/AdminDashboard.jsx — Admin Dashboard (Firebase)
// ============================================================

import React, { useState, useEffect } from "react";
import { useBookings, useCustomers, useProducts } from "../../hooks/useFirestore";
import { collection, query, orderBy, getDocs, updateDoc, doc, deleteDoc } from "firebase/firestore";
import { db } from "../../firebase.config";

export default function AdminDashboard() {
  const { bookings } = useBookings(true);
  const { customers } = useCustomers();
  const { products } = useProducts(true);
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const q = query(collection(db, "contactMessages"), orderBy("createdAt", "desc"));
        const snap = await getDocs(q);
        setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.warn("Could not fetch messages:", err.message);
      }
    };
    fetchMessages();
  }, []);

  const markRead = async (id) => {
    await updateDoc(doc(db, "contactMessages", id), { isRead: true });
    setMessages((prev) => prev.map((m) => m.id === id ? { ...m, isRead: true } : m));
  };

  const deleteMessage = async (id) => {
    await deleteDoc(doc(db, "contactMessages", id));
    setMessages((prev) => prev.filter((m) => m.id !== id));
  };

  const totalRevenue = bookings
    .filter((b) => ["completed", "delivered"].includes(b.status))
    .reduce((sum, b) => sum + (b.total || 0), 0);
  const pendingCount = bookings.filter((b) => b.status === "pending").length;
  const completedCount = bookings.filter((b) => ["completed", "delivered"].includes(b.status)).length;
  const todayBookings = bookings.filter((b) => {
    const d = b.pickupDate?.toDate ? b.pickupDate.toDate() : new Date(b.pickupDate);
    const today = new Date();
    return d.toDateString() === today.toDateString();
  });

  const recentBookings = bookings.slice(0, 8);

  // Top customers by spend
  const topCustomers = [...customers]
    .sort((a, b) => (b.totalSpent || 0) - (a.totalSpent || 0))
    .slice(0, 5);

  const formatDate = (ts) => {
    if (!ts) return "—";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
  };

  return (
    <div>
      <div className="section-header">
        <div>
          <h2>Admin Dashboard</h2>
          <p>Real-time overview of your bakery operations</p>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Total Revenue</div>
          <div className="stat-value">Rs {totalRevenue.toLocaleString()}</div>
        </div>
        <div className="stat-card sage">
          <div className="stat-label">Total Orders</div>
          <div className="stat-value">{bookings.length}</div>
        </div>
        <div className="stat-card berry">
          <div className="stat-label">Pending</div>
          <div className="stat-value">{pendingCount}</div>
        </div>
        <div className="stat-card mocha">
          <div className="stat-label">Completed</div>
          <div className="stat-value">{completedCount}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Today's Orders</div>
          <div className="stat-value">{todayBookings.length}</div>
        </div>
        <div className="stat-card sage">
          <div className="stat-label">Customers</div>
          <div className="stat-value">{customers.length}</div>
        </div>
        <div className="stat-card berry">
          <div className="stat-label">Products</div>
          <div className="stat-value">{products.length}</div>
        </div>
      </div>

      <div className="two-col">
        {/* Recent bookings */}
        <div>
          <h3 style={{ fontFamily: "'Playfair Display', serif", marginBottom: "1rem" }}>
            Recent Bookings
          </h3>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr><th>Booking</th><th>Customer</th><th>Date</th><th>Total</th><th>Status</th></tr>
              </thead>
              <tbody>
                {recentBookings.map((b) => (
                  <tr key={b.id}>
                    <td style={{ fontFamily: "monospace", fontSize: "0.82rem" }}>
                      {b.bookingNumber || b.id.slice(0, 8)}
                    </td>
                    <td>{b.customerName}</td>
                    <td>{formatDate(b.pickupDate)}</td>
                    <td style={{ fontWeight: 700 }}>Rs {b.total || 0}</td>
                    <td>
                      <span className={`status-badge status-${b.status?.replace("_", "-")}`}>
                        {b.status?.replace("_", " ")}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top customers */}
        <div>
          <h3 style={{ fontFamily: "'Playfair Display', serif", marginBottom: "1rem" }}>
            Top Customers
          </h3>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr><th>Name</th><th>Orders</th><th>Spent</th><th>Tier</th></tr>
              </thead>
              <tbody>
                {topCustomers.map((c) => (
                  <tr key={c.id}>
                    <td>{c.displayName || c.email}</td>
                    <td>{c.totalOrders || 0}</td>
                    <td style={{ fontWeight: 700 }}>Rs {(c.totalSpent || 0).toLocaleString()}</td>
                    <td>
                      <span className="inline-tag" style={{ textTransform: "capitalize" }}>
                        {c.loyaltyTier || "bronze"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Contact Messages */}
      {messages.length > 0 && (
        <div style={{ marginTop: "2rem" }}>
          <h3 style={{ fontFamily: "'Playfair Display', serif", marginBottom: "1rem" }}>
            Contact Messages ({messages.filter((m) => !m.isRead).length} unread)
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            {messages.map((m) => (
              <div key={m.id} className="card" style={{
                padding: "1rem 1.25rem",
                background: m.isRead ? "var(--card-bg)" : "rgba(196,136,58,0.04)",
                borderLeft: m.isRead ? "none" : "3px solid var(--chai)",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.5rem" }}>
                  <div>
                    <strong style={{ fontSize: "0.92rem" }}>{m.name}</strong>
                    <span style={{ fontSize: "0.82rem", color: "var(--cocoa)", marginLeft: "0.75rem" }}>{m.email}</span>
                  </div>
                  <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                    <span style={{ fontSize: "0.72rem", color: "var(--cocoa)" }}>
                      {m.createdAt?.toDate ? m.createdAt.toDate().toLocaleDateString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                    </span>
                    {!m.isRead && (
                      <button className="btn btn-outline btn-sm" onClick={() => markRead(m.id)}>Mark Read</button>
                    )}
                    <button className="btn btn-danger btn-sm" onClick={() => deleteMessage(m.id)}>Delete</button>
                  </div>
                </div>
                <p style={{ fontSize: "0.88rem", color: "var(--cocoa)", lineHeight: 1.6, margin: 0 }}>{m.message}</p>
                <a href={`mailto:${m.email}`} style={{ fontSize: "0.82rem", color: "var(--chai)", marginTop: "0.5rem", display: "inline-block" }}>
                  Reply via email →
                </a>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}